import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import MaklanDivisions from "@/components/site/MaklanDivisions";
import EditorialMarquee from "@/components/site/EditorialMarquee";
import ManifestoChapters from "@/components/site/ManifestoChapters";
import MaklanStandard from "@/components/site/MaklanStandard";
import GlobalNetwork from "@/components/site/GlobalNetwork";
import PartnerWithUs from "@/components/site/PartnerWithUs";
import MaklanMonogram from "@/components/site/MaklanMonogram";
import { RevealLines, RevealOnScroll } from "@/components/site/Reveal";
import { ArrowRight } from "lucide-react";

const STORY = [
  { year: "2025", title: "The Question", body: "Why should Africa always receive the world's products last, most expensive, and worst supported? A quiet company was founded to change the answer." },
  { year: "2026", title: "The First House", body: "Motors — the automotive division of Maklan — opens its doors in Accra, with a considered collection of premium imports and a promise of concierge care." },
  { year: "2026/27", title: "The Wider Circle", body: "Energy, Home, Tech and Machinery are prepared as their own houses — each with its own considered standard." },
  { year: "Onward", title: "A Legacy", body: "Maklan is built to be a company visitors' grandchildren will still recognise. Every partnership we sign is chosen with that horizon in mind." },
];

const WHY_AFRICA = [
  { title: "The Fastest-Growing Continent", body: "Africa's middle class is expected to double within a generation. The world's finest makers will need a trusted counterpart on the ground." },
  { title: "A Market That Deserves Better", body: "For too long the continent has received second-tier products, thin warranty coverage and slow service. Maklan is the counter-proposition." },
  { title: "A Company Built From Here", body: "We are African by origin, global by network. That combination is the reason we exist." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="pt-40 pb-32 relative overflow-hidden">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-16 items-end">
            <div className="lg:col-span-8">
              <div className="flex items-center gap-4 mb-10" data-testid="hero-parent-label">
                <span className="text-[10px] tracking-[0.5em] uppercase text-white/50">A House of</span>
                <span
                  className="font-display uppercase text-[#C5A059] text-[1.35rem] md:text-[1.6rem] leading-none"
                  style={{ letterSpacing: "0.42em" }}
                >
                  Maklan
                </span>
              </div>
              <RevealLines
                element="h1"
                className="h-editorial text-[clamp(2.6rem,7vw,7rem)]"
                lines={["A quiet company,", "with a global <em>ambition</em>."]}
              />
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.8 }}
                className="mt-14 max-w-2xl text-white/75 text-lg font-light leading-relaxed"
              >
                Maklan is a premium global sourcing and commerce company connecting
                Africa with the world's finest manufacturers through trusted
                international partnerships. Our mission is simple. Bring the
                world to Africa — quietly, elegantly, reliably.
              </motion.p>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <MaklanMonogram size={90} tone="gold" />
            </div>
          </div>
        </div>
      </section>

      <EditorialMarquee />

      {/* MISSION / VISION / PHILOSOPHY */}
      <section className="py-28 md:py-40 border-t border-white/5">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="grid md:grid-cols-3 gap-px bg-white/10">
            {[
              { label: "Mission", body: "To connect Africa with the world's finest products through relationships that outlive the transaction." },
              { label: "Vision", body: "A continent served by a house of brands the world knows to trust — beginning with Maklan." },
              { label: "Philosophy", body: "Quiet excellence, patiently pursued. Every promise we make is a promise we intend to keep for decades." },
            ].map((c) => (
              <div key={c.label} className="bg-[#050814] p-10 md:p-14">
                <div className="overline mb-6">{c.label}</div>
                <p className="font-display text-2xl md:text-3xl leading-snug text-white/90">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ManifestoChapters />

      {/* STORY / TIMELINE */}
      <section data-testid="story-section" className="relative py-28 md:py-40 border-t border-white/5">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="max-w-3xl mb-20">
            <div className="overline mb-6">The Maklan Story</div>
            <RevealOnScroll
              element="h2"
              className="h-editorial text-[clamp(2.4rem,5.5vw,5rem)]"
              lines={["A company built", "for the <em>long horizon</em>."]}
            />
          </div>
          <ol className="relative border-l border-white/10 ml-3 space-y-16">
            {STORY.map((s, i) => (
              <motion.li
                key={s.year}
                initial={{ opacity: 0, x: -12 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.8, delay: i * 0.08 }}
                className="pl-10 md:pl-16 relative"
              >
                <span className="absolute -left-[7px] top-2 w-3.5 h-3.5 rotate-45 border border-[#C5A059] bg-[#050814]" />
                <div className="grid md:grid-cols-12 gap-6 md:gap-10">
                  <div className="md:col-span-3">
                    <div className="text-[#C5A059] font-display italic text-3xl">{s.year}</div>
                  </div>
                  <div className="md:col-span-9">
                    <div className="font-display text-2xl md:text-3xl leading-snug">{s.title}</div>
                    <div className="w-10 h-px bg-white/20 my-4" />
                    <p className="text-white/70 leading-relaxed max-w-2xl">{s.body}</p>
                  </div>
                </div>
              </motion.li>
            ))}
          </ol>
        </div>
      </section>

      <MaklanStandard />

      {/* GLOBAL SUPPLY PHILOSOPHY */}
      <section className="relative py-28 md:py-40 border-t border-white/5">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <div className="overline mb-6">Global Supply Philosophy</div>
            <RevealOnScroll
              element="h2"
              className="h-editorial text-[clamp(2.2rem,5vw,4.5rem)]"
              lines={["Sourced with", "<em>intent</em>."]}
            />
          </div>
          <div className="lg:col-span-7 space-y-8 text-white/75 text-lg font-light leading-relaxed">
            <p>Maklan does not broker. We build direct, long-standing relationships
              with makers we believe in — and we invest in the ports, corridors and
              partners that carry their work to the continent.</p>
            <p>Every shipment is accompanied. Every warranty is honoured. Every product
              is chosen because we would place it in our own home, our own garage,
              our own office.</p>
            <p className="font-display italic text-[#C5A059] text-2xl">
              "If we would not stand behind it, we do not stand in front of it."
            </p>
          </div>
        </div>
      </section>

      <GlobalNetwork />

      {/* WHY AFRICA */}
      <section className="relative py-28 md:py-40 border-t border-white/5">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="max-w-2xl mb-16">
            <div className="overline mb-6">Why Africa</div>
            <RevealOnScroll
              element="h2"
              className="h-editorial text-[clamp(2.2rem,5vw,4.5rem)]"
              lines={["A market that", "deserves <em>better</em>."]}
            />
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {WHY_AFRICA.map((w, i) => (
              <motion.div
                key={w.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.8, delay: i * 0.08 }}
                className="border border-white/10 p-8 hover:border-[#C5A059]/50 transition-colors duration-500"
              >
                <div className="text-[#C5A059] font-display italic text-2xl">{["I","II","III"][i]}</div>
                <div className="font-display text-2xl mt-6">{w.title}</div>
                <div className="w-8 h-px bg-white/20 my-4" />
                <p className="text-white/60 leading-relaxed text-sm">{w.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FUTURE ROADMAP */}
      <section id="roadmap" className="relative py-28 md:py-40 border-t border-white/5">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="max-w-2xl mb-16">
            <div className="overline mb-6">Future Roadmap</div>
            <RevealOnScroll
              element="h2"
              className="h-editorial text-[clamp(2.2rem,5vw,4.5rem)]"
              lines={["What follows,", "and <em>when</em>."]}
            />
          </div>
          <div className="grid md:grid-cols-4 gap-px bg-white/10">
            {[
              { phase: "Now", body: "Motors — the automotive division — is open and taking enquiries." },
              { phase: "Next", body: "Extending the Motors collection into commercial, motorcycles and service." },
              { phase: "Soon", body: "Energy and Home divisions prepared for launch, each with their own house." },
              { phase: "Onward", body: "Tech and Machinery — the long horizon of the Maklan project." },
            ].map((r, i) => (
              <div key={r.phase} className="bg-[#050814] p-8">
                <div className="text-[#C5A059] font-display italic text-2xl">{r.phase}</div>
                <div className="w-8 h-px bg-white/20 my-4" />
                <p className="text-white/70 text-sm leading-relaxed">{r.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <MaklanDivisions />

      <PartnerWithUs />

      {/* Closing */}
      <section className="py-32 md:py-40 border-t border-white/5">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10 text-center">
          <div className="overline mb-6">In Closing</div>
          <RevealOnScroll
            element="h2"
            className="h-editorial text-[clamp(2.6rem,7vw,6rem)]"
            lines={["We are not building a", "<em>company</em>. We are", "building a <em>standard</em>."]}
          />
          <div className="mt-12 flex flex-wrap gap-3 justify-center">
            <Link to="/contact" className="btn-gold">Contact Maklan <ArrowRight className="ml-2" size={14}/></Link>
            <Link to="/partner" className="btn-ghost">Partner With Us</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
